import { pushAnalyticsEvent } from "@/lib/analytics/dataLayer";
const PUBLIC_PATHS = new Set(["/", "/local-lessons", "/singing-lessons-ely", "/singing-lessons-bury-st-edmunds", "/contact", "/about-liuba", "/success-stories", "/method", "/terms", "/privacy", "/cookies", "/blog", "/coach-network"]);
function publicAnalyticsPath(value: unknown): string | null {
  return typeof value === "string" && PUBLIC_PATHS.has(value) ? value : null;
}

export type AnalyticsSite = 'com' | 'uk';
export type DomainHandoffIntent = 'local_lessons' | 'online_evaluation' | 'online_video_review' | 'online_coaching';
export type DomainHandoffProperties = {
  from_site: AnalyticsSite; to_site: AnalyticsSite; intent: DomainHandoffIntent;
  source_page: string; destination_path: string;
};
const DESTINATIONS = {
  local_lessons: {site:'uk',paths:['/','/local-lessons','/singing-lessons-ely','/singing-lessons-bury-st-edmunds']},
  online_evaluation: {site:'com',paths:['/evaluation','/evaluation/paid']},
  online_video_review: {site:'com',paths:['/video-feedback']},
  online_coaching: {site:'com',paths:['/online-singing-lessons','/coaching']},
} as const;
export function domainHandoffProperties(properties?: Record<string, unknown>): DomainHandoffProperties | null {
  if (!properties || (properties.from_site !== 'com' && properties.from_site !== 'uk') ||
      (properties.to_site !== 'com' && properties.to_site !== 'uk') || properties.from_site === properties.to_site ||
      typeof properties.intent !== 'string' || !Object.prototype.hasOwnProperty.call(DESTINATIONS,properties.intent)) return null;
  const intent = properties.intent as DomainHandoffIntent;
  const destination = DESTINATIONS[intent];
  const source = publicAnalyticsPath(properties.source_page);
  if (!source || properties.to_site !== destination.site || typeof properties.destination_path !== 'string' ||
      !(destination.paths as readonly string[]).includes(properties.destination_path)) return null;
  return {from_site:properties.from_site,to_site:properties.to_site,intent,source_page:source,destination_path:properties.destination_path};
}

/** Validate the actual public link. Its query/hash never enter analytics and href is never decorated. */
export function buildDomainHandoffProperties(input: {
  href: string; fromSite: AnalyticsSite; intent: DomainHandoffIntent; sourcePage: string;
}): DomainHandoffProperties | null {
  try {
    const url = new URL(input.href);
    if (url.protocol !== 'https:' || url.username || url.password || url.port) return null;
    const toSite = ['singingattitude.com','www.singingattitude.com'].includes(url.hostname) ? 'com'
      : ['singingattitude.uk','www.singingattitude.uk'].includes(url.hostname) ? 'uk' : null;
    return domainHandoffProperties({from_site:input.fromSite,to_site:toSite,intent:input.intent,source_page:input.sourcePage,destination_path:url.pathname});
  } catch { return null; }
}

/** Track only the explicitly linked online offers, with an unchanged destination URL. */
export function trackUkDomainHandoff(href: string, sourcePage: string) {
  let intent: DomainHandoffIntent;
  try {
    const path = new URL(href).pathname;
    if (path === "/evaluation" || path === "/evaluation/paid") intent = "online_evaluation";
    else if (path === "/video-feedback") intent = "online_video_review";
    else if (path === "/coaching" || path === "/online-singing-lessons") intent = "online_coaching";
    else return;
  } catch { return; }
  const properties = buildDomainHandoffProperties({ href, fromSite: "uk", intent, sourcePage });
  if (properties) pushAnalyticsEvent("domain_handoff", properties);
}
